import { computed, inject, Injectable, signal } from '@angular/core';
import { ReplaySubject } from 'rxjs';
import { distinctUntilChanged, first, map, skip, tap } from 'rxjs/operators';
import { CmsApiService } from '../api/cms-api.service';
import { UserService } from '../user/user.service';
import { LanguageService } from '../language.service';
import { HostRequestsInterceptor } from '../../interceptors/host-requests.interceptor';
import { environment } from '../../../../environments/environment';
import { GameAttrType, GameCategoryAttrType, GameCategoryLocation, GameCategoryType } from '../../types/game.type';
import { GameCategoryEnum } from '../../enums/game-category.enum';

@Injectable({
  providedIn: 'root',
})
export class GameCategoryAttrService {
  private _api = inject(CmsApiService);
  private _user = inject(UserService);
  private _lang = inject(LanguageService);
  private _hostInterceptor = inject(HostRequestsInterceptor);

  private readonly AUTH_ONLY_CATEGORY_IDS: number[] = [GameCategoryEnum.FAVORITE_ID, GameCategoryEnum.LAST_PLAYED_ID];
  private readonly AUTH_ONLY_CATEGORY_SLUGS: string[] = [GameCategoryEnum.FAVORITE, GameCategoryEnum.LAST_PLAYED];

  /**
   * Game category list observable
   */
  public categoryList$: ReplaySubject<GameCategoryType[]> = new ReplaySubject<GameCategoryType[]>(1);

  /**
   * Game attr list observable
   */
  public attrList$: ReplaySubject<GameAttrType[]> = new ReplaySubject<GameAttrType[]>(1);
  private _categoryAttrList = signal<GameCategoryAttrType[]>([]);

  public filterCategoryAttrList = computed(() => this._getSearchCategoryAttrList());

  public lobbyCategoryAttrList = computed(() => this._getSortedCategoryAttrListByLocation('lobby'));
  public homePageCategoryAttrList = computed(() => this._getSortedCategoryAttrListByLocation('home_page'));
  public sideMenuCategoryAttrList = computed(() => this._getSortedCategoryAttrListByLocation('side_menu'));

  /**
   * Subscribes to language change events and reloads category and attribute lists
   * Ensures category and attribute data is updated when the language changes
   */
  public updateCategoryAttrListOnLangChange() {
    this.loadCategoryAttrList();

    this._lang.langChange$.subscribe(() => {
      this.loadCategoryAttrList();
    });

    this._user.auth$.pipe(skip(1), distinctUntilChanged()).subscribe(() => {
      this.loadCategoryAttrList();
    });
  }

  /**
   * Loads game category and attribute lists from the API
   * Retrieves list with optional country parameter for authenticated users
   * Updates local signals and replay subjects with fetched data
   * Converts category and attribute IDs to numbers and sets additional properties
   */
  public loadCategoryAttrList(): void {
    const params = this._user.auth ? { country: this._user.info.country } : {};
    this._api
      .gameCategoryAttrList(params)
      .pipe(
        first(),
        map((response) => response.data),
        map(({ categoryList, attributeList }) => ({
          categoryList: categoryList.map((c) => this._mapCategory(c)),
          attributeList: attributeList.map((a) => this._mapAttribute(a)),
        })),
        map(({ categoryList, attributeList }) => {
          const categoryAttrList = [...categoryList, ...attributeList];

          this._categoryAttrList.set(categoryAttrList);

          this.categoryList$.next(categoryList);
          this.attrList$.next(attributeList);

          return categoryAttrList;
        }),
      )
      .subscribe();
  }

  private _mapCategory(category: any): GameCategoryAttrType {
    return {
      ...category,
      id: +category.id,
      sortOrder: this._resolveSortOrder(category),
      imgSrc: this._resolveImgSrcField(category),
      svgImg: category.svgImg || category.svg_img,
      location: this._normalizeLocations(category.location),
      image: this._resolveImage(category),
      type: 'category',
    };
  }

  private _mapAttribute(attribute: any): GameCategoryAttrType {
    return {
      ...attribute,
      id: +attribute.id,
      slug: attribute.slug || attribute.short,
      sortOrder: this._resolveSortOrder(attribute),
      imgSrc: this._resolveImgSrcField(attribute),
      svgImg: attribute.svgImg || attribute.svg_img,
      location: this._normalizeLocations(attribute.location),
      image: this._resolveImage(attribute),
      type: 'attribute',
    };
  }

  private _getSearchCategoryAttrList(): GameCategoryAttrType[] {
    return this._sortAlphabetically(this._getAvailableCategoryAttrListByLocation('search'));
  }

  private _getSortedCategoryAttrListByLocation(location: GameCategoryLocation): GameCategoryAttrType[] {
    return this._sortBySortOrder(this._getAvailableCategoryAttrListByLocation(location));
  }

  private _getAvailableCategoryAttrListByLocation(location: GameCategoryLocation): GameCategoryAttrType[] {
    return this._filterAvailableCategoryAttrList(this._filterByLocation(this._categoryAttrList(), location));
  }

  private _filterAvailableCategoryAttrList(list: GameCategoryAttrType[]): GameCategoryAttrType[] {
    return this._user.auth ? list : list.filter((item) => !this._isAuthOnlyCategory(item));
  }

  private _isAuthOnlyCategory(item: GameCategoryAttrType): boolean {
    return item.type === 'category' && (this.AUTH_ONLY_CATEGORY_IDS.includes(item.id) || this.AUTH_ONLY_CATEGORY_SLUGS.includes(item.slug || ''));
  }

  private _filterByLocation<
    T extends {
      location?: GameCategoryLocation[] | null;
    },
  >(list: T[], location: GameCategoryLocation): T[] {
    return list.filter((item) => !item.location?.length || item.location.includes(location));
  }

  private _sortBySortOrder<T extends { sortOrder?: number | string; name?: string }>(list: T[]): T[] {
    return list.slice().sort((a, b) => {
      const orderDiff = this._sortOrderValue(a) - this._sortOrderValue(b);
      return orderDiff || this._compareByName(a, b);
    });
  }

  private _sortAlphabetically<T extends { name?: string }>(list: T[]): T[] {
    return list.slice().sort((a, b) => this._compareByName(a, b));
  }

  private _compareByName(a: { name?: string }, b: { name?: string }): number {
    return this._normalizeName(a?.name).localeCompare(this._normalizeName(b?.name), undefined, {
      sensitivity: 'base',
      numeric: true,
    });
  }

  private _normalizeName(name?: string): string {
    return String(name || '').trim();
  }

  private _sortOrderValue(item: { sortOrder?: number | string }): number {
    const sortOrder = Number(item?.sortOrder);
    return Number.isFinite(sortOrder) ? sortOrder : Number.MAX_SAFE_INTEGER;
  }

  private _resolveSortOrder(item: any): number | string {
    return item?.sortOrder ?? item?.sort_order;
  }

  private _resolveImgSrcField(item: any): string {
    return item?.imgSrc || item?.img_src;
  }

  private _normalizeLocations(location: GameCategoryLocation[] | GameCategoryLocation | string | null | undefined): GameCategoryLocation[] | null {
    if (!location) return null;
    if (Array.isArray(location)) return location.filter(Boolean) as GameCategoryLocation[];
    const locationString = String(location).trim();

    if (locationString.startsWith('[')) {
      try {
        const parsedLocation = JSON.parse(locationString);
        if (Array.isArray(parsedLocation)) return parsedLocation.filter(Boolean) as GameCategoryLocation[];
      } catch {
      }
    }

    return locationString
      .split(',')
      .map((item) => item.trim())
      .filter(Boolean) as GameCategoryLocation[];
  }

  private _resolveImage(item: any): string {
    const image = this._resolveImgSrcField(item) || item?.svgImg || item?.svg_img;
    if (!image) return null;

    const trimmed = String(image).trim();
    if (trimmed.startsWith('<svg')) {
      return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(trimmed)}`;
    }
    if (/^(https?:)?\/\//.test(trimmed) || trimmed.startsWith('data:') || trimmed.startsWith('/assets/')) {
      return trimmed;
    }
    return this._hostInterceptor.overrideUrl(environment.assets_host + trimmed);
  }
}
